// src/auth/useAuth.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { User } from '../lib/types'

export function useSession() {
  return useQuery<{ user: User }, Error>({
    queryKey: ['session'],
    queryFn: () => api.get<{ user: User }>('/api/auth/session'),
    retry: false,
    staleTime: 1000 * 60 * 5,
  })
}

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation<
    { user: User },
    Error,
    { email: string; password: string }
  >({
    mutationFn: (credentials) =>
      api.post<{ user: User }>('/api/auth/login', credentials),
    onSuccess: (data) => {
      queryClient.setQueryData(['session'], data)
    },
  })
}

export function useChangePassword() {
  return useMutation<
    { ok: boolean },
    Error,
    { currentPassword: string; newPassword: string }
  >({
    mutationFn: (payload) =>
      api.post<{ ok: boolean }>('/api/auth/change-password', payload),
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, void>({
    mutationFn: () => api.post<void>('/api/auth/logout', {}),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['session'] })
      window.location.href = '/login'
    },
  })
}
